import { Alert, Box, Chip, CircularProgress, Divider, Grid, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { ReactElement, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import toastOptions from '../../helpers/toastOptions';
import {
  getMessagingSettings,
  getMessagingSettingsInfo,
  messagingSettingsFetching,
  messagingSettingsUpdating,
  updateMessagingSettings,
} from '../../redux/slices/messaging/messagingSlice';
import { useDispatch, useSelector } from '../../redux/store';
import { Card, CardHeaderTabs } from '../../styled/components';

const SettingRow = ({ label, value }: { label: string; value: ReactElement | string | undefined }): ReactElement => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" py={1.5}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {typeof value === 'string' || value === undefined ? <Typography variant="body2">{value || '—'}</Typography> : value}
  </Stack>
);

const YesNo = ({ value }: { value?: boolean }): ReactElement => <Chip label={value ? 'Yes' : 'No'} color={value ? 'success' : 'default'} size="small" />;

export const Communications = (): ReactElement => {
  const dispatch = useDispatch();
  const settings = useSelector(getMessagingSettingsInfo);
  const fetching = useSelector(messagingSettingsFetching);
  const updating = useSelector(messagingSettingsUpdating);

  useEffect(() => {
    dispatch(getMessagingSettings());
  }, [dispatch]);

  const switchProvider = useCallback(
    async (channel: 'email' | 'sms', provider: string | null) => {
      if (!provider || !settings) return;
      try {
        await dispatch(
          updateMessagingSettings({
            email_provider: channel === 'email' ? provider : settings.email_provider,
            sms_provider: channel === 'sms' ? provider : settings.sms_provider,
          }),
        ).unwrap();
        toast.success(`${channel === 'email' ? 'Email' : 'SMS'} provider switched to ${provider} (until restart)`, toastOptions);
      } catch (error) {
        console.log(error); // eslint-disable-line
      }
    },
    [dispatch, settings],
  );

  const providerToggle = (channel: 'email' | 'sms'): ReactElement => {
    const current = channel === 'email' ? settings?.email_provider : settings?.sms_provider;
    const awsAvailable = channel === 'email' ? settings?.aws?.email_available : settings?.aws?.sms_available;
    const awsReason = channel === 'email' ? settings?.aws?.email_reason : settings?.aws?.sms_reason;
    return (
      <ToggleButtonGroup value={current} exclusive size="small" disabled={!!updating} onChange={(event, value) => switchProvider(channel, value)}>
        <ToggleButton value="legacy">{channel === 'email' ? 'SendGrid' : 'Twilio'} (legacy)</ToggleButton>
        {awsAvailable ? (
          <ToggleButton value="aws">AWS</ToggleButton>
        ) : (
          <Tooltip title={awsReason || 'AWS is not configured'}>
            <span>
              <ToggleButton value="aws" disabled>
                AWS
              </ToggleButton>
            </span>
          </Tooltip>
        )}
      </ToggleButtonGroup>
    );
  };

  return (
    <>
      {fetching && <LinearProgress />}
      <Card>
        <CardHeaderTabs
          title={
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={0}>
                <Tab label="Communications" />
              </Tabs>
            </Box>
          }
        />
        <Box px={6} pb={6}>
          {!settings && fetching && (
            <Box py={8} textAlign="center">
              <CircularProgress size={24} />
            </Box>
          )}
          {settings && (
            <>
              {settings.runtime_override && (
                <Alert severity="warning" sx={{ mb: 4 }}>
                  Temporary override active — providers revert to the environment configuration (email=
                  {settings.env_email_provider ?? settings.env_provider}, SMS={settings.env_sms_provider ?? settings.env_provider}) when the container restarts.
                </Alert>
              )}
              <Alert severity="warning" sx={{ mb: 4 }}>
                {settings.runtime_override_scope ?? 'Runtime provider switches are in-memory and apply only to the container that served this request.'}
              </Alert>
              {!settings.send_enabled && (
                <Alert severity="info" sx={{ mb: 4 }}>
                  Sending is disabled in this environment (SEND_INVITE=false) — provider switches take effect but all messages are logged as skipped.
                </Alert>
              )}

              <Typography variant="h6" gutterBottom>
                Active providers
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Runtime switch for testing only. The environment value ({settings.env_provider}) is restored on restart; change the ECS task definition to make
                it permanent.
              </Typography>
              <SettingRow label="Email" value={providerToggle('email')} />
              <Divider />
              <SettingRow label="SMS" value={providerToggle('sms')} />

              <Grid container spacing={6} mt={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>
                    General
                  </Typography>
                  <Divider />
                  <SettingRow label="Sending enabled" value={<YesNo value={settings.send_enabled} />} />
                  <SettingRow label="Environment provider" value={settings.env_provider} />
                  <SettingRow label="Environment email provider" value={settings.env_email_provider ?? settings.env_provider} />
                  <SettingRow label="Environment SMS provider" value={settings.env_sms_provider ?? settings.env_provider} />
                  <SettingRow label="From name" value={settings.mail_from_name} />
                  <SettingRow label="From email" value={settings.mail_from_email} />
                  <SettingRow label="Email root link" value={settings.email_root_link} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>
                    Legacy — SendGrid &amp; Twilio
                  </Typography>
                  <Divider />
                  <SettingRow label="SendGrid API key" value={<YesNo value={settings.legacy?.sendgrid_api_key_set} />} />
                  <SettingRow label="Twilio account SID" value={settings.legacy?.twilio_sid} />
                  <SettingRow label="Twilio auth token" value={<YesNo value={settings.legacy?.twilio_token_set} />} />
                  <SettingRow label="Twilio from number" value={settings.legacy?.twilio_from} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>
                    AWS — SES &amp; End User Messaging
                  </Typography>
                  <Divider />
                  <SettingRow label="Region" value={settings.aws?.region} />
                  <SettingRow
                    label="Email (SES)"
                    value={
                      settings.aws?.email_available ? (
                        <Chip label="Available" color="success" size="small" />
                      ) : (
                        <Tooltip title={settings.aws?.email_reason || ''}>
                          <Chip label="Unavailable" size="small" />
                        </Tooltip>
                      )
                    }
                  />
                  <SettingRow
                    label="SMS (End User Messaging)"
                    value={
                      settings.aws?.sms_available ? (
                        <Chip label="Available" color="success" size="small" />
                      ) : (
                        <Tooltip title={settings.aws?.sms_reason || ''}>
                          <Chip label="Unavailable" size="small" />
                        </Tooltip>
                      )
                    }
                  />
                  <SettingRow label="SMS origination identity" value={settings.aws?.sms_origination_identity} />
                  <SettingRow label="SES configuration set" value={settings.aws?.ses_configuration_set} />
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Card>
    </>
  );
};

export default Communications;
